import {
  selectProfile,
  setShowProfileModal,
  setShowProfileSidebar,
  fetchPracticeProfileData,
  fetchPublicPracticesData,
  ShowProfileModalType,
  fetchPublicProviderProfileData,
} from "../domain/Profile/model"
import { mapSearchResultToProviderProfileData } from "../domain/Profile/searchResultMapper"
import { useSelector } from "react-redux"
import { useAppDispatch } from "../reduxStore/hooks"
import { useCallback, useState } from "react"
import { selectValidatedHealthAdvocateUser } from "./shared/selectors"
import { Provider } from "../domain/Provider/schema"
import { PublicPractice, PublicProfilePractice } from "../domain/Profile/schema"
import { DataStatus } from "../domain/enums"

type HandleSetShowProfileArgs = {
  practiceId?: string
  provider?: {
    id: string
    userId: string
    prefetched?: ProcessedProviderSearchResults
  }
}

export const useSlideOver = () => {
  const dispatch = useAppDispatch()
  const [dataStatus, setDataStatus] = useState(DataStatus.LOADING)
  const { showProfileModal, showProfileSidebar } = useSelector(selectProfile)
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)

  const handleSetShowProfileModal = useCallback(
    (showProfileModalArgs?: ShowProfileModalType) => {
      if (!!showProfileModalArgs) {
        dispatch(setShowProfileModal(showProfileModalArgs))
      } else {
        dispatch(setShowProfileModal())
      }
    },
    [dispatch],
  )

  type getDataReturnType = {
    providerProfile?: Provider
    publicPracticeProfiles?: PublicPractice[]
    practiceProfile?: PublicProfilePractice
  }

  const getData = useCallback(
    async (args: HandleSetShowProfileArgs): Promise<getDataReturnType> => {
      const { practiceId, provider } = args
      const getDataResult: getDataReturnType = {
        providerProfile: undefined,
        publicPracticeProfiles: undefined,
        practiceProfile: undefined,
      }
      if (practiceId) {
        getDataResult.practiceProfile = await fetchPracticeProfileData(
          practiceId,
          isHaUser,
          setDataStatus,
        )
      }
      if (provider) {
        if (provider.prefetched) {
          // Search-driven open: hydrate from the index, no DB calls.
          const hydrated = mapSearchResultToProviderProfileData(
            provider.prefetched,
          )
          getDataResult.providerProfile = hydrated.provider
          getDataResult.publicPracticeProfiles = hydrated.publicPractices
          setDataStatus(DataStatus.LOADED)
        } else {
          const [providerProfile, publicPracticeProfiles] = await Promise.all([
            fetchPublicProviderProfileData(provider, setDataStatus),
            fetchPublicPracticesData(provider, setDataStatus),
          ])
          getDataResult.providerProfile = providerProfile
          getDataResult.publicPracticeProfiles = publicPracticeProfiles
        }
      }
      return getDataResult
    },
    [isHaUser],
  )

  const handleSetShowProfileSidebar = useCallback(
    async (args?: HandleSetShowProfileArgs & { scrollToSection?: string }) => {
      const { practiceId, provider } = args || {}
      if (practiceId) {
        dispatch(setShowProfileSidebar({ practiceId: practiceId }))
      }
      if (provider) {
        dispatch(
          setShowProfileSidebar({
            provider: {
              id: provider.id,
              userId: provider.userId,
              prefetched: provider.prefetched,
            },
            scrollToSection: args?.scrollToSection,
          }),
        )
      }
      if (!practiceId && !provider) {
        dispatch(
          setShowProfileSidebar({ provider: undefined, practiceId: undefined }),
        )
      }
    },
    [dispatch],
  )

  return {
    showProfileModal,
    showProfileSidebar,
    handleSetShowProfileModal,
    handleSetShowProfileSidebar,
    getData,
    dataStatus,
  }
}
